import React, { useMemo, useState, useCallback } from 'react';
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  List
} from '@mui/material';
import makeStyles from '@mui/styles/makeStyles';
import { FormattedMessage } from 'react-intl';
import { FormProvider } from 'react-hook-form';
import { DateTime } from 'luxon';
import classnames from 'classnames';
import { TimeSignal, TimeSignalClassification } from 'graphql/types';
import { useDialogStyles } from 'modules/notes/addNote/AddNoteDialog';
import {
  useIsBreakpointDown,
  useDialogCancelAction
} from 'modules/common/hooks';
import { NoteDialogFooter } from 'modules/common/components';
import { useScrollbarStyles } from 'modules/common/styles';
import {
  useFormChangeHandlers,
  useTimeSignalAttributes
} from 'modules/notes/addNote/hooks';
import {
  TimesheetLabels,
  LabelsFormFieldContextProvider
} from 'modules/timesheet-labels';
import { FormTimesheetLabel } from 'modules/timesheet-labels/labelSchema';
import { TimerContextProvider, AttributesContent } from 'modules/notes/common';
import { CustomLabels } from 'modules/customLabels/components/CustomLabels';
import { NoteListItem } from 'modules/notes/list/NoteListItem';
import { NoteContextProvider } from 'modules/notes/NoteContext';
import { NOTE_FIELDS } from 'modules/notes/common/noteSchema';
import { useNotesFilterContext } from 'modules/notes/NotesFilterContextProvider';
import { GroupEditBody } from './GroupEditBody';
import { useGroupEditForm, useGroupEditAction } from './hooks';

export const messages = {
  cancelDialogTitle: <FormattedMessage id="note.groupEdit.cancelTitle" />,
  saveButtonMessage: <FormattedMessage id="note.groupEdit.groupEditTo" />,
  cancelDialogBodyMessages: [
    <FormattedMessage
      key="note.groupEdit.cancelMessage1"
      id="note.groupEdit.cancelMessage1"
    />,
    <FormattedMessage
      key="note.groupEdit.cancelMessage2"
      id="note.groupEdit.cancelMessage2"
    />
  ]
};

const useStyles = makeStyles((theme) => ({
  title: {
    ...theme.typography.h6,
    padding: theme.spacing(2, 2, 0, 3),
    color: theme.palette.grey[700],
    display: 'flex'
  },
  content: {
    padding: theme.spacing(0)
  },
  divider: {
    backgroundColor: theme.palette.grey[100]
  },
  moreOrLess: {
    textTransform: 'none',
    padding: theme.spacing(0, 2),
    margin: theme.spacing(0, 1)
  },
  notesInfoListContainer: {
    maxHeight: theme.spacing(20),
    overflowY: 'auto',
    backgroundColor: theme.palette.table?.header,
    paddingLeft: theme.spacing(3)
  },
  notesInfoList: {
    position: 'relative',
    listStyle: 'none',
    paddingLeft: theme.spacing(1),
    '&:nth-child(1) li:first-child::after': {
      height: 0
    },
    padding: theme.spacing(0, 2)
  },
  footer: {
    padding: theme.spacing(1, 3.75, 1, 2.5)
  },
  listItem: {
    paddingLeft: theme.spacing(2),
    '&:before': {
      content: '""',
      position: 'absolute',
      left: theme.spacing(-1),
      borderTop: `2px solid ${theme.palette.grey[600]}`,
      top: '50%',
      width: theme.spacing(2),
      height: 0,
      backgoundColor: 'orange'
    },
    '&:after': {
      content: '""',
      position: 'absolute',
      left: theme.spacing(-1),
      borderLeft: `2px solid ${theme.palette.grey[600]}`,
      top: '-50%',
      width: '0px',
      height: '100%',
      backgoundColor: 'yellow'
    }
  },
  moreBtn: {
    marginLeft: `${theme.spacing(2.5)} !important`
  }
}));

interface GroupEditDialogProps {
  open: boolean;
  onClose: () => void;
  notes: TimeSignal[];
  primaryNote: TimeSignal;
}
export const GroupEditDialog: React.FC<GroupEditDialogProps> = ({
  open,
  onClose,
  notes: initialNotes,
  primaryNote
}: GroupEditDialogProps) => {
  const fullScreen = useIsBreakpointDown('xs');

  /* eslint-disable-next-line */
  const notes = useMemo(() => initialNotes, []);
  const values = useMemo(() => ({ count: notes.length }), [notes]);

  const [expanded, setExpanded] = useState(true);
  const handleOnMoreOrLess = useCallback(() => {
    setExpanded((exp) => !exp);
  }, []);

  const {
    formMethods,
    timesheetLabelFields,
    errorMessage,
    labelsLoading,
    isFormDirty,
    isValidForm
  } = useGroupEditForm(notes, primaryNote);

  const { isActionInProgress, groupEditAction } = useGroupEditAction({
    notes,
    getValues: formMethods.getValues
  });

  const cancelActionProps = useDialogCancelAction({
    loading: isActionInProgress,
    onClose,
    isFormDirty,
    isValidForm
  });

  const formChangeHandlers = useFormChangeHandlers(formMethods);
  const noteDateTime = useMemo(() => DateTime.fromISO(notes[0].date), [notes]);
  const noteDate = useMemo(() => noteDateTime.toJSDate(), [noteDateTime]);
  const [primaryNoteId, setPrimaryNoteId] = useState(primaryNote.id);
  const { attributes } = useTimeSignalAttributes(primaryNoteId);
  const classes = useStyles();
  const classesScrollbar = useScrollbarStyles();

  const {
    filterContext: { selectedClassification }
  } = useNotesFilterContext();

  const classification = useMemo(
    () =>
      selectedClassification !== 'All'
        ? TimeSignalClassification[selectedClassification]
        : undefined,
    [selectedClassification]
  );

  const noteListItemClasses = useMemo(
    () => ({
      root: classes.listItem
    }),
    [classes.listItem]
  );

  return (
    //to fix type instantiation is excessively deep and infinite
    // eslint-disable-next-line @typescript-eslint/ban-ts-comment
    // @ts-ignore
    <FormProvider {...formMethods}>
      <NoteContextProvider date={noteDateTime}>
        <TimerContextProvider hideTimer>
          <Dialog
            data-testid="group-edit-dialog"
            open={open}
            onClose={onClose}
            fullScreen={fullScreen}
            classes={useDialogStyles()}
          >
            <DialogTitle
              className={classes.title}
              data-testid="group-edit-dialog-title"
            >
              <FormattedMessage id="note.groupEdit.title" values={values} />
              <Button
                data-testid="group-edit-more-less"
                className={classes.moreOrLess}
                onClick={handleOnMoreOrLess}
              >
                <FormattedMessage
                  id={
                    expanded
                      ? 'note.groupEdit.lessDetails'
                      : 'note.groupEdit.moreDetails'
                  }
                />
              </Button>
            </DialogTitle>
            <DialogContent
              className={classnames(
                classes.content,
                classesScrollbar.scrollbar
              )}
              data-testid={'group-edit-dialog-content'}
            >
              {expanded && (
                <>
                  <Divider
                    className={classes.divider}
                    orientation="horizontal"
                  />
                  <div
                    className={classnames(
                      classes.notesInfoListContainer,
                      classesScrollbar.scrollbar
                    )}
                  >
                    <List className={classes.notesInfoList}>
                      {notes.map((note) => (
                        <NoteListItem
                          key={note.id}
                          note={note}
                          readOnly
                          classes={noteListItemClasses}
                        />
                      ))}
                    </List>
                  </div>
                  <Divider
                    className={classes.divider}
                    orientation="horizontal"
                  />
                </>
              )}

              <GroupEditBody
                errorMessage={errorMessage}
                formChangeHandlers={formChangeHandlers}
                date={noteDate}
                notes={notes}
                attributes={attributes}
                primaryNoteId={primaryNoteId}
                setPrimaryNoteId={setPrimaryNoteId}
                expanded={expanded}
              />
              <CustomLabels fieldName={NOTE_FIELDS.CUSTOM_LABELS} />
              <AttributesContent
                attributes={attributes}
                moreClassName={classes.moreBtn}
              />
              <LabelsFormFieldContextProvider
                timesheetLabels={NOTE_FIELDS.TIMESHEET_LABELS}
                note={NOTE_FIELDS.NOTE}
              >
                <TimesheetLabels
                  loading={labelsLoading}
                  fields={timesheetLabelFields as FormTimesheetLabel[]}
                />
              </LabelsFormFieldContextProvider>
            </DialogContent>
            <DialogActions
              className={classes.footer}
              data-testid="group-edit-dialog-footer"
            >
              <NoteDialogFooter
                {...cancelActionProps}
                {...messages}
                onSave={groupEditAction}
                isActionInProgress={isActionInProgress}
                selectedClassification={classification}
                customLabels={NOTE_FIELDS.CUSTOM_LABELS}
                timesheetLabels={NOTE_FIELDS.TIMESHEET_LABELS}
              />
            </DialogActions>
          </Dialog>
        </TimerContextProvider>
      </NoteContextProvider>
    </FormProvider>
  );
};
